import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type DataLabel,
  type GrantResponse,
  type LabelPermission,
  lakeApi,
  type UserInfo,
} from '@/services/api/lake'

export interface NewLabelInput {
  name: string
  description: string
  level: number
  color: string | null
}

function errMsg(err: unknown, fallback: string): string {
  return (err as { message?: string })?.message || fallback
}

/** Data-label administration: labels, the selected label's grants, the user
 *  pool for assignment, and all CRUD actions over lakeApi (toast + refetch). */
export function useLabelsAdmin() {
  const { t } = useTranslation()
  const [labels, setLabels] = useState<DataLabel[]>([])
  const [labelsLoading, setLabelsLoading] = useState(true)
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null)
  const [grants, setGrants] = useState<GrantResponse[]>([])
  const [grantsLoading, setGrantsLoading] = useState(false)
  const [users, setUsers] = useState<UserInfo[]>([])

  const fetchLabels = useCallback(async () => {
    try {
      setLabelsLoading(true)
      setLabels(await lakeApi.listLabels())
    } catch {
      toast.error(t('admin.labels.failedToLoad'))
    } finally {
      setLabelsLoading(false)
    }
  }, [t])

  const fetchGrants = useCallback(
    async (labelId: string) => {
      try {
        setGrantsLoading(true)
        setGrants(await lakeApi.listGrants(labelId))
      } catch {
        toast.error(t('admin.labels.failedToLoadGrants'))
      } finally {
        setGrantsLoading(false)
      }
    },
    [t]
  )

  const fetchUsers = useCallback(async () => {
    try {
      setUsers(await lakeApi.listUsers())
    } catch {
      // silent — only needed for the grant-assignment combo
    }
  }, [])

  useEffect(() => {
    fetchLabels()
    fetchUsers()
  }, [fetchLabels, fetchUsers])

  useEffect(() => {
    if (selectedLabelId) fetchGrants(selectedLabelId)
    else setGrants([])
  }, [selectedLabelId, fetchGrants])

  const selectedLabel = labels.find(l => l.id === selectedLabelId) || null

  const createLabel = async (input: NewLabelInput): Promise<boolean> => {
    try {
      await lakeApi.createLabel({
        name: input.name.trim(),
        description: input.description.trim() || null,
        level: input.level,
        color: input.color || null,
      })
      toast.success(`Label "${input.name}" created`)
      await fetchLabels()
      return true
    } catch (err) {
      toast.error(errMsg(err, t('admin.labels.failedToCreate')))
      return false
    }
  }

  const deleteLabel = async (label: DataLabel) => {
    try {
      await lakeApi.deleteLabel(label.id)
      toast.success(`Label "${label.name}" deleted`)
      if (selectedLabelId === label.id) setSelectedLabelId(null)
      await fetchLabels()
    } catch (err) {
      toast.error(errMsg(err, t('admin.labels.failedToDelete')))
    }
  }

  const addGrant = async (username: string, permission: LabelPermission): Promise<boolean> => {
    if (!selectedLabelId) return false
    try {
      await lakeApi.createGrant(selectedLabelId, { username: username.trim(), permission })
      toast.success(`Access granted to "${username}"`)
      await fetchGrants(selectedLabelId)
      return true
    } catch (err) {
      toast.error(errMsg(err, t('admin.labels.failedToAddGrant')))
      return false
    }
  }

  const updateGrant = async (grant: GrantResponse, permission: LabelPermission) => {
    if (!selectedLabelId) return
    try {
      await lakeApi.updateGrant(selectedLabelId, grant.id, { username: grant.username, permission })
      toast.success(`Updated permission for "${grant.username}"`)
      await fetchGrants(selectedLabelId)
    } catch (err) {
      toast.error(errMsg(err, t('admin.labels.failedToUpdateGrant')))
    }
  }

  const revokeGrant = async (grant: GrantResponse) => {
    if (!selectedLabelId) return
    try {
      await lakeApi.revokeGrant(selectedLabelId, grant.id)
      toast.success(`Revoked access for "${grant.username}"`)
      await fetchGrants(selectedLabelId)
    } catch (err) {
      toast.error(errMsg(err, t('admin.labels.failedToRevokeGrant')))
    }
  }

  return {
    labels,
    labelsLoading,
    selectedLabelId,
    setSelectedLabelId,
    selectedLabel,
    grants,
    grantsLoading,
    users,
    createLabel,
    deleteLabel,
    addGrant,
    updateGrant,
    revokeGrant,
  }
}
